import { z } from 'zod'
import { PRIORITIES } from './domain.ts'

// Черновик задачи, полученный из текста/голосового/пересылки. Никогда не создаётся без подтверждения.

export const DRAFT_SOURCES = ['text', 'voice', 'forward', 'group'] as const
export type DraftSource = (typeof DRAFT_SOURCES)[number]

export const DraftAssignee = z.object({
  /** self — себе; resolved — однозначно найден; ambiguous — несколько кандидатов; unknown — имя не найдено; none — не указан. */
  kind: z.enum(['self', 'resolved', 'ambiguous', 'unknown', 'none']),
  userId: z.number().int().positive().nullable(),
  candidateIds: z.array(z.number().int().positive()).max(10),
  /** Как человека назвали в источнике («Ване», «Петрову из продаж»). */
  mention: z.string().max(100),
  /** Ещё исполнители общей задачи («Ваня и Петя, сделайте…») — найденные однозначно. */
  extraUserIds: z.array(z.number().int().positive()).max(10).optional(),
})
export type DraftAssignee = z.infer<typeof DraftAssignee>

/** Подпункт черновика; assigneeId — за кем пункт (если назвали однозначно). */
export const DraftChecklistItem = z.object({
  text: z.string().min(1).max(200),
  assigneeId: z.number().int().positive().nullable(),
})
export type DraftChecklistItem = z.infer<typeof DraftChecklistItem>

export const DRAFT_DUE_FLAGS = [
  'checked',
  'shifted_from_weekend',
  'time_passed_moved',
  'past_dropped',
] as const

export const DraftDue = z.object({
  /** Фраза из источника дословно («до пятницы»). */
  phrase: z.string().max(100),
  at: z.iso.datetime({ offset: true }).nullable(),
  kind: z.enum(['date', 'datetime']),
  flags: z.array(z.enum(DRAFT_DUE_FLAGS)).max(4),
})
export type DraftDue = z.infer<typeof DraftDue>

export const TaskDraft = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(4000).nullable(),
  dod: z.string().max(300).nullable(),
  assignee: DraftAssignee,
  due: DraftDue,
  priority: z.enum(PRIORITIES),
  /** Цитата из источника, на основе которой создан черновик. */
  sourceQuote: z.string().max(400),
  confidence: z.number().min(0).max(1),
  /** Подпункты («1) … 2) …», перечисление шагов). */
  checklist: z.array(DraftChecklistItem).max(30).optional(),
  /** Клиент или проект: найденный (id) или новое название — проект создастся при создании задачи. */
  project: z
    .object({ id: z.number().int().positive().nullable(), name: z.string().min(1).max(100) })
    .optional(),
})
export type TaskDraft = z.infer<typeof TaskDraft>

export const DraftBatchResult = z.object({
  transcript: z.string().nullable(),
  drafts: z.array(TaskDraft).max(10),
  truncated: z.boolean(),
  notes: z.string().max(500),
})
export type DraftBatchResult = z.infer<typeof DraftBatchResult>
